import { useQuery } from "@tanstack/react-query";
import { useEsAdmin } from "@/hooks/useEsAdmin";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, Building2, FileWarning, Mail, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import {
  DetailLayout,
  DetailLoading,
  DetailError,
  Section,
  Field,
  StatusBadge,
  formatDate,
} from "@/components/DetailLayout";

interface CartaDetalle {
  id: string;
  titularId: string;
  titularRazonSocial: string;
  titularRfc: string;
  tramitadorId: string | null;
  tramitadorRazonSocial: string | null;
  documentoId: string | null;
  documentoNombreArchivo: string | null;
  esCopiaCertificadaOriginal: boolean;
  vigenciaInicio: string | null;
  vigenciaFin: string | null;
  sinVencimiento: boolean;
  estatus: string;
  fechaCreacion: string;
}

interface UsuarioAutorizado {
  asociacionId: string;
  usuarioId: string;
  correo: string;
  nombreCompleto: string;
  tipoAutorizacion: string;
  estatusAsociacion: string;
  sectoresCodigos: string[];
}

function tonePorEstatus(estatus: string): "ok" | "info" | "warn" | "neutral" {
  if (estatus === "VIGENTE") return "ok";
  if (estatus === "REVOCADA" || estatus === "VENCIDA") return "warn";
  return "info";
}

function tonePorTipoAuth(tipo: string): "ok" | "info" | "warn" | "neutral" {
  switch (tipo) {
    case "FIRMA":
      return "ok";
    case "GESTION":
    case "INTEGRAL":
      return "info";
    case "CONSULTA":
    case "SEGUIMIENTO":
      return "neutral";
    default:
      return "info";
  }
}

function labelTipoAuth(tipo: string): string {
  return ({
    GESTION: "Gestión",
    FIRMA: "Firma",
    CONSULTA: "Consulta",
    SEGUIMIENTO: "Seguimiento",
    INTERNO_TITULAR: "Interno del titular",
  } as Record<string, string>)[tipo] ?? tipo;
}

export function CartaPoderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const esAdmin = useEsAdmin();
  const q = useQuery({
    queryKey: ["carta-poder", id],
    queryFn: async () => (await api.get<CartaDetalle>(`/api/cartas-poder/${id}`)).data,
    enabled: !!id,
  });
  const qUsuarios = useQuery({
    queryKey: ["carta-poder", id, "usuarios"],
    queryFn: async () => (await api.get<UsuarioAutorizado[]>(`/api/cartas-poder/${id}/usuarios`)).data,
    enabled: !!id,
  });

  if (q.isLoading) return <DetailLoading />;
  if (q.error || !q.data) return <DetailError />;
  const c = q.data;
  const usuarios = qUsuarios.data ?? [];

  const sinDocumento = !c.documentoId;
  const exigeOriginal = !c.esCopiaCertificadaOriginal;

  return (
    <DetailLayout
      title="Carta poder"
      subtitle={
        <span className="text-sm text-fg-muted">
          Documento legal que delega facultades del titular hacia un tramitador externo.
        </span>
      }
      backTo="/tramites-legales"
      backLabel="Volver"
      badges={
        <>
          <StatusBadge tone={tonePorEstatus(c.estatus)}>{c.estatus}</StatusBadge>
          {sinDocumento && <StatusBadge tone="warn">sin documento PDF</StatusBadge>}
          {c.sinVencimiento && <StatusBadge tone="info">sin vencimiento</StatusBadge>}
          {c.esCopiaCertificadaOriginal && <StatusBadge tone="ok">copia certificada</StatusBadge>}
        </>
      }
      rawData={c}
    >
      {/* Flujo visual de delegación */}
      <section className="rounded-lg border bg-surface p-4 shadow-card">
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <PartyCard
            rol="Otorga el poder"
            razonSocial={c.titularRazonSocial}
            rfc={c.titularRfc}
            link={`/registros-legales/${c.titularId}`}
            tone="primary"
          />
          <div className="flex items-center justify-center text-fg-faint">
            <ArrowRight className="h-6 w-6" />
          </div>
          <PartyCard
            rol="Recibe el poder"
            razonSocial={c.tramitadorRazonSocial ?? "(sin tramitador)"}
            rfc={null}
            link={c.tramitadorId ? `/registros-legales/${c.tramitadorId}` : null}
            tone="secondary"
          />
        </div>
      </section>

      {/* Banner de advertencias críticas */}
      {(sinDocumento || (exigeOriginal && c.estatus === "VIGENTE")) && (
        <section className="space-y-2">
          {sinDocumento && (
            <Alert
              tone="warn"
              icon={<FileWarning className="h-5 w-5" />}
              titulo="Carta sin documento PDF vinculado"
              detalle="Esta carta poder está vigente en BD pero no tiene el documento firmado adjunto. Vincúlalo desde el módulo documental antes de validar trámites que dependan de ella."
            />
          )}
          {exigeOriginal && !sinDocumento && (
            <Alert
              tone="info"
              icon={<AlertTriangle className="h-5 w-5" />}
              titulo="Versión vigente NO es copia certificada original"
              detalle="Para Telecom (RL-RN-103) la carta debe ser copia certificada original. Solicita al titular subir la versión correcta."
            />
          )}
        </section>
      )}

      {/* Usuarios autorizados */}
      <Section title={`Usuarios autorizados vía esta carta (${usuarios.length})`}>
        <Field
          label=""
          span={3}
          value={
            qUsuarios.isLoading ? (
              <span className="text-xs text-fg-subtle">Cargando…</span>
            ) : usuarios.length === 0 ? (
              <span className="text-sm text-fg-subtle">
                Aún no hay usuarios asociados a esta carta. Calidad puede asociarlos desde el detalle del Registro Legal.
              </span>
            ) : (
              <ul className="divide-y rounded border">
                {usuarios.map((u) => (
                  <li key={u.asociacionId} className="flex flex-wrap items-center justify-between gap-2 p-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-fg">{u.nombreCompleto || u.correo}</span>
                        <StatusBadge tone={tonePorTipoAuth(u.tipoAutorizacion)}>
                          {labelTipoAuth(u.tipoAutorizacion)}
                        </StatusBadge>
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-fg-subtle">
                        <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{u.correo}</span>
                        {u.sectoresCodigos.length > 0 && (
                          <span className="inline-flex flex-wrap gap-1">
                            {u.sectoresCodigos.map((s) => (
                              <span key={s} className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[10px]">
                                {s}
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )
          }
        />
      </Section>

      {/* Vigencia */}
      <Section title="Vigencia">
        <Field label="Inicia" value={formatDate(c.vigenciaInicio)} />
        <Field
          label="Termina"
          value={
            c.sinVencimiento ? (
              <span className="inline-flex items-center gap-1 text-sm">
                <ShieldCheck className="h-4 w-4 text-estado-info" />
                Sin vencimiento
              </span>
            ) : (
              formatDate(c.vigenciaFin)
            )
          }
        />
        <Field label="Estatus" value={<StatusBadge tone={tonePorEstatus(c.estatus)}>{c.estatus}</StatusBadge>} />
        {c.sinVencimiento && (
          <Field
            label=""
            span={3}
            value={
              <p className="text-xs text-estado-info">
                Las cartas <strong>sin vencimiento</strong> requieren motivo justificado y autorización de un usuario
                distinto al capturista (segregación de funciones, RL-RN-104). Consulta la auditoría del RL titular para el detalle.
              </p>
            }
          />
        )}
      </Section>

      {/* Documento de respaldo */}
      <Section title="Documento de respaldo">
        {sinDocumento ? (
          <Field
            label=""
            span={3}
            value={
              <div className="rounded border border-estado-warn/25 bg-estado-warn-bg p-3 text-sm text-estado-warn">
                Esta carta no tiene PDF vinculado. Para vincularlo, sube el documento desde el detalle del Registro Legal
                titular en la sección de documentos requeridos.
              </div>
            }
          />
        ) : (
          <>
            <Field label="Archivo" value={c.documentoNombreArchivo ?? "—"} span={2} />
            <Field
              label="Copia certificada"
              value={
                c.esCopiaCertificadaOriginal ? (
                  <span className="inline-flex items-center gap-1 text-estado-ok">
                    <ShieldCheck className="h-4 w-4" /> Sí (original)
                  </span>
                ) : (
                  <span className="text-estado-warn">No</span>
                )
              }
            />
          </>
        )}
      </Section>

      <Section title="Registro">
        {/* ID interno: solo para ADMIN_SICEN (soporte) */}
        {esAdmin && <Field label="ID" value={c.id} mono span={2} />}
        <Field label="Fecha de alta" value={formatDate(c.fechaCreacion, true)} />
      </Section>
    </DetailLayout>
  );
}

function PartyCard({
  rol,
  razonSocial,
  rfc,
  link,
  tone,
}: {
  rol: string;
  razonSocial: string;
  rfc: string | null;
  link: string | null;
  tone: "primary" | "secondary";
}) {
  const cls =
    tone === "primary"
      ? "border-primary-strong/40 bg-primary-tint"
      : "border-edge bg-surface-2";
  return (
    <div className={`flex-1 rounded-lg border p-3 ${cls}`}>
      <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-fg-subtle">
        <Building2 className="h-3 w-3" /> {rol}
      </div>
      <div className="mt-1 text-sm font-semibold text-fg">
        {link ? (
          <Link to={link} className="hover:underline">
            {razonSocial}
          </Link>
        ) : (
          razonSocial
        )}
      </div>
      {rfc && <div className="mt-0.5 font-mono text-xs text-fg-subtle">{rfc}</div>}
    </div>
  );
}

function Alert({
  tone,
  icon,
  titulo,
  detalle,
}: {
  tone: "warn" | "info";
  icon: React.ReactNode;
  titulo: string;
  detalle: string;
}) {
  const cls =
    tone === "warn"
      ? "border-estado-warn/25 bg-estado-warn-bg text-estado-warn"
      : "border-estado-info/25 bg-estado-info-bg text-estado-info";
  return (
    <div className={`flex items-start gap-3 rounded-lg border p-3 ${cls}`}>
      <div className="shrink-0">{icon}</div>
      <div className="text-sm">
        <p className="font-medium">{titulo}</p>
        <p className="mt-0.5 text-xs">{detalle}</p>
      </div>
    </div>
  );
}
